/**
 * The dungeon level format: what the builder writes and what the game client and the
 * battle server read. Plain JSON only -- no engine or network types leak in here, so a
 * level can be stored, diffed and shared as-is.
 *
 * Two formats live in this file, and they are not two versions of one document.
 * `DungeonLevel` is §10's v2: a short list of placed blocks, which is what a level is
 * now. `TileLevel` is the tile-kind grid 0.1.0 shipped, kept under its own name because
 * the admin editor's three parked tile-editor files still compile against it.
 *
 * **A v1 level does not migrate.** §10 of develop/DungeonBlocks.md: a tile grid cannot be
 * reconstructed as functional rooms, so `readLevel` refuses one by name rather than
 * inventing the rooms a migration would have to invent. Nothing authored in v1 exists
 * beyond a sample and a localStorage draft.
 */

// Type-only on purpose, both ways: `modules/blocks.ts` type-imports `TileRotation` from
// here, so this is a cycle -- and `verbatimModuleSyntax` erases both halves of it, which
// is the only reason it is harmless. A *value* imported either way would make it real.
import type { BlockRotation, Cell, PlacedBlock } from './modules/blocks.ts';
import type { BlockCatalogue } from './modules/placement.ts';

/** Bumped whenever a stored level can no longer be read as-is. v1 is the tile grid. */
export const DUNGEON_LEVEL_FORMAT_VERSION = 2;

/** The format the tile grid below is stored at. It is read by nothing and refused by name. */
export const TILE_LEVEL_FORMAT_VERSION = 1;

/**
 * World units (metres) per grid cell. Dungeons are authored on a grid, not free-form.
 *
 * 5 m matches the Synty modular art: every pack's floor tiles measure 5 m, with some
 * 2.5 m half-modules. See develop/ArtLibrary.md §2.
 *
 * Note the art is authored corner-origin (a tile spans 0..5, not -2.5..+2.5) while
 * `tileToWorld` below returns cell centres. That is correct for the placeholder boxes
 * and will need reconciling when the real meshes load.
 */
export const TILE_SIZE = 5;

/**
 * A level: which blocks, where, and how turned. Everything else about it is derived --
 * `resolveLevel` in `./modules/placement.ts` is the one place that derives it.
 *
 * `defaultSkin` and `groups` (§6.2) are deliberately absent until skins exist, and the
 * per-instance `variant`, `skin`, `locks` and `holdsKey` of §10 with them: a field that
 * nothing writes and nothing reads is a field a reader has to guess the rules for.
 */
export interface DungeonLevel {
  formatVersion: typeof DUNGEON_LEVEL_FORMAT_VERSION;
  id: string;
  name: string;
  /** Player id of the builder who authored the level. */
  authorId: string;
  blocks: PlacedBlock[];
  /** ISO-8601. */
  createdAt: string;
}

export function createEmptyLevel(overrides: Partial<DungeonLevel> = {}): DungeonLevel {
  return {
    formatVersion: DUNGEON_LEVEL_FORMAT_VERSION,
    id: `lvl_${Math.random().toString(36).slice(2, 10)}`,
    name: 'Untitled Dungeon',
    authorId: 'anonymous',
    blocks: [],
    createdAt: new Date().toISOString(),
    ...overrides,
  };
}

/** What `readLevel` says about a document: the level, or one sentence naming the reason. */
export type LevelReadResult =
  | { ok: true; level: DungeonLevel }
  | { ok: false; reason: string };

const BLOCK_ROTATIONS: readonly BlockRotation[] = [0, 90, 180, 270];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCell(value: unknown): value is Cell {
  if (!isRecord(value)) return false;
  return Number.isInteger(value['x']) && Number.isInteger(value['z']);
}

function isBlockRotation(value: unknown): value is BlockRotation {
  return BLOCK_ROTATIONS.some((allowed) => allowed === value);
}

/** A required non-empty string field, or `undefined` when it is neither. */
function readText(value: Record<string, unknown>, field: string): string | undefined {
  const got = value[field];
  return typeof got === 'string' && got !== '' ? got : undefined;
}

/**
 * Read a stored or pasted document as a v2 level.
 *
 * Never throws and never migrates: a caller gets a level it can draw, or a sentence it
 * can put in a status line. Pass the `catalogue` and a block id it does not hold is
 * refused too -- a level naming blocks this build has no definitions for would draw as
 * holes, which is worse than not opening.
 */
export function readLevel(value: unknown, catalogue?: BlockCatalogue): LevelReadResult {
  if (typeof value === 'string') {
    try {
      return readLevel(JSON.parse(value) as unknown, catalogue);
    } catch {
      return { ok: false, reason: 'That is not JSON.' };
    }
  }

  if (!isRecord(value)) return { ok: false, reason: 'A level must be a JSON object.' };

  const formatVersion = value['formatVersion'];
  if (formatVersion === TILE_LEVEL_FORMAT_VERSION) {
    return {
      ok: false,
      reason:
        'Level format v1 is the tile grid, and it does not migrate: a grid of tile kinds ' +
        'cannot be read back as functional rooms. Build this one again from blocks.',
    };
  }
  if (formatVersion !== DUNGEON_LEVEL_FORMAT_VERSION) {
    return {
      ok: false,
      reason: `Level format v${String(formatVersion)} is not one this build reads; it reads v${DUNGEON_LEVEL_FORMAT_VERSION}.`,
    };
  }

  const id = readText(value, 'id');
  if (id === undefined) return { ok: false, reason: 'A level needs an "id".' };
  const name = readText(value, 'name');
  if (name === undefined) return { ok: false, reason: 'A level needs a "name".' };
  const authorId = readText(value, 'authorId');
  if (authorId === undefined) return { ok: false, reason: 'A level needs an "authorId".' };
  const createdAt = readText(value, 'createdAt');
  if (createdAt === undefined) return { ok: false, reason: 'A level needs a "createdAt".' };

  const blocks = value['blocks'];
  if (!Array.isArray(blocks)) return { ok: false, reason: 'A level needs a "blocks" array.' };

  const seen = new Set<string>();
  const placed: PlacedBlock[] = [];
  for (let index = 0; index < blocks.length; index += 1) {
    const where = `block ${index + 1} of ${blocks.length}`;
    const block: unknown = blocks[index];
    if (!isRecord(block)) return { ok: false, reason: `${where} is not an object.` };

    const instanceId = block['instanceId'];
    if (typeof instanceId !== 'string' || instanceId === '') {
      return { ok: false, reason: `${where} has no "instanceId".` };
    }
    // A lock or a key names a block by its instance id (§10), so two blocks sharing one
    // is a level that cannot say which it meant.
    if (seen.has(instanceId)) {
      return { ok: false, reason: `Two blocks share the instance id "${instanceId}".` };
    }
    seen.add(instanceId);

    const blockId = block['block'];
    if (typeof blockId !== 'string' || blockId === '') {
      return { ok: false, reason: `${where} ("${instanceId}") names no block.` };
    }
    if (catalogue && !catalogue[blockId]) {
      return { ok: false, reason: `No block definition for "${blockId}", named by ${where}.` };
    }

    const at = block['at'];
    if (!isCell(at)) {
      return { ok: false, reason: `${where} ("${instanceId}") has no whole-cell "at".` };
    }

    const rotation = block['rotation'];
    if (!isBlockRotation(rotation)) {
      return {
        ok: false,
        reason: `${where} ("${instanceId}") is turned ${String(rotation)}, not one of 0, 90, 180, 270.`,
      };
    }

    const mirrored = block['mirrored'];
    if (mirrored !== undefined && typeof mirrored !== 'boolean') {
      return { ok: false, reason: `${where} ("${instanceId}") has a non-boolean "mirrored".` };
    }

    // Rebuilt field by field rather than spread, so a field a later format adds is left
    // behind by default instead of riding in unvalidated -- the rule the character pack
    // manifest's reader follows for the same reason.
    const next: PlacedBlock = {
      instanceId,
      block: blockId,
      at: { x: at.x, z: at.z },
      rotation,
    };
    if (mirrored !== undefined) next.mirrored = mirrored;
    placed.push(next);
  }

  return {
    ok: true,
    level: {
      formatVersion: DUNGEON_LEVEL_FORMAT_VERSION,
      id,
      name,
      authorId,
      blocks: placed,
      createdAt,
    },
  };
}

export const TILE_KINDS = ['floor', 'wall', 'door', 'spawn', 'exit', 'trap', 'chest'] as const;
export type TileKind = (typeof TILE_KINDS)[number];

/** Tiles snap to 90-degree rotations so modular art pack pieces line up. */
export type TileRotation = 0 | 90 | 180 | 270;

export interface DungeonTile {
  /** Grid column. */
  x: number;
  /** Grid row. */
  z: number;
  kind: TileKind;
  rotation: TileRotation;
  /** Art pack prefab id; unset means "use the default prop for this kind". */
  prefab?: string;
}

/**
 * The v1 format: a grid of tile kinds. Superseded by `DungeonLevel` above and read by
 * nothing that ships -- it stays so the admin editor's parked tile-editor files
 * (`editor/levelEditor.ts`, `editor/levelStore.ts`, `editor/tilePalette.ts`) compile
 * until level authoring is picked up on blocks.
 */
export interface TileLevel {
  formatVersion: number;
  id: string;
  name: string;
  /** Player id of the builder who authored the level. */
  authorId: string;
  /** Grid extents, in cells. */
  width: number;
  height: number;
  tiles: DungeonTile[];
  /** ISO-8601. */
  createdAt: string;
}

export function createEmptyTileLevel(overrides: Partial<TileLevel> = {}): TileLevel {
  return {
    formatVersion: TILE_LEVEL_FORMAT_VERSION,
    id: `lvl_${Math.random().toString(36).slice(2, 10)}`,
    name: 'Untitled Dungeon',
    authorId: 'anonymous',
    width: 16,
    height: 16,
    tiles: [],
    createdAt: new Date().toISOString(),
    ...overrides,
  };
}

/** Grid cell -> world-space centre of that cell. */
export function tileToWorld(x: number, z: number): { x: number; z: number } {
  return { x: x * TILE_SIZE, z: z * TILE_SIZE };
}

/** World-space position -> the grid cell containing it. */
export function worldToTile(x: number, z: number): { x: number; z: number } {
  return { x: Math.round(x / TILE_SIZE), z: Math.round(z / TILE_SIZE) };
}
